import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";
import { fmtCurrency } from "../lib/formatters";
import { useAutoDismiss } from "../hooks/useAutoDismiss";
import { useAuth, canEdit, canEditAssociation } from "../contexts/AuthContext";
import { useHoverIntent } from "../lib/DelayedTooltip";
import OpeningBalanceModal from "./OpeningBalanceModal";

// Per-year detail popover — opened on CLICK, dismissed on outside click / ×.
// -----------------------------------------------------------------------
// When barName === "Expenses" we render a WIDE MASTER-DETAIL two-pane
// layout (Dave 2026-08-06, replacing the previous cascade approach that
// felt inconsistent + clunky):
//
//   • Left pane  — the year list (year | inflated total, coral for
//                   non-zero). One row is "selected" at any time; clicking
//                   a different year updates the right pane.
//   • Right pane — the elements that made up the selected year's total,
//                   sorted DESCENDING by inflated amount, name + amount.
//
// One popover, one X, no cascade, no back-arrow. The right pane is
// deterministic: it always shows the currently-selected year with no
// flip logic, so behavior is predictable regardless of screen position.
function YearlyDetailPopover({ barName, slice, calc, onClose, anchor }) {
  const ref = useRef(null);
  const [selectedYear, setSelectedYear] = useState(null);
  // 2026-02-19 (Dave) — auto-dismiss after `hover_visible_seconds`
  // (default 6 s) so click-popups feel consistent with hover tooltips.
  useAutoDismiss(true, onClose);
  // Viewport-clamped position, computed post-mount so we know the actual
  // popover dimensions. Starts hidden (opacity 0) to avoid a flash.
  const [placed, setPlaced] = useState({ left: anchor.x, top: anchor.y + 8, opacity: 0 });
  useEffect(() => {
    const h = (e) => {
      if (!ref.current?.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  // Clamp the popover inside the viewport once we know its rendered size.
  // Prefers to center-horizontally around the click, then clamps to the
  // window bounds so it never overflows off-screen — which is the root
  // cause the previous cascade felt "inconsistent + clunky".
  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    let left = anchor.x - rect.width / 2;
    let top = anchor.y + 12;
    left = Math.max(8, Math.min(left, vw - rect.width - 8));
    if (top + rect.height > vh - 8) {
      top = Math.max(8, anchor.y - rect.height - 12);
    }
    top = Math.max(8, top);
    setPlaced({ left, top, opacity: 1 });
  }, [anchor.x, anchor.y, barName, selectedYear]);

  const S = calc.display?.horizons?.[String(slice.length)] || {};
  const beginning = S.beginning || 0;
  const ending = S.ending || 0;
  const totals = {
    Beginning: beginning,
    Contributions: S.total_contributions || 0,
    Interest: S.total_interest || 0,
    Expenses: S.total_expenditures || 0,
    "Special Asmt": S.total_special_assessments || 0,
    Ending: ending,
  };
  const cfg = {
    Beginning: { total: totals.Beginning, single: true, note: `Opening balance ${slice[0]?.year || ""}` },
    Contributions: { total: totals.Contributions, key: "contribution", note: `${slice.length}-yr sum` },
    Interest: { total: totals.Interest, key: "interest", note: `${slice.length}-yr sum of interest earned` },
    Expenses: { total: totals.Expenses, key: "expenditures", note: "Inflated element replacements", accent: true },
    "Special Asmt": { total: totals["Special Asmt"], key: "special_assessment", note: "Years triggering a special assessment", onlyNonZero: true },
    Ending: { total: totals.Ending, single: true, note: `Closing balance ${slice[slice.length - 1]?.year || ""}` },
  }[barName];
  if (!cfg) return null;

  const rows = cfg.single
    ? []
    : slice.map((r) => ({ year: r.year, v: r[cfg.key] || 0 }))
        .filter((r) => !cfg.onlyNonZero || r.v > 0.5);

  // For the Expenses master-detail view, auto-select the biggest year on
  // open so the right pane is never empty. If there ARE no non-zero years
  // (edge case at very short horizons), fall back to null and the right
  // pane shows a soft empty state.
  const isExpensesMaster = barName === "Expenses";
  const defaultYear = isExpensesMaster ? (S.peak_nonzero_expense_year ?? null) : null;
  const activeYear = selectedYear ?? defaultYear;
  const activeYearCal = isExpensesMaster && activeYear != null
    ? (calc?.expenditure_calendar || []).find((c) => c.year === activeYear)
    : null;
  const activeYearElements = (activeYearCal?.elements || [])
    .filter((el) => (el.amount || 0) > 0.5)
    .slice()
    .sort((a, b) => (b.amount || 0) - (a.amount || 0));

  // Popover width — 560px for the two-pane Expenses view, 220px minimum
  // for the single-pane views used by the other bars.
  const popWidth = isExpensesMaster ? 560 : undefined;
  const popMaxH = isExpensesMaster ? 400 : 340;

  const popoverEl = (
    <div
      ref={ref}
      data-testid={`waterfall-popover-${barName.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white shadow-lg"
      style={{
        position: "fixed", zIndex: 60,
        left: placed.left, top: placed.top, opacity: placed.opacity,
        border: "1px solid #1C4E80",
        borderRadius: 3,
        fontSize: 11, minWidth: 220, width: popWidth, maxHeight: popMaxH,
        overflow: "hidden", display: "flex", flexDirection: "column",
        transition: "opacity 120ms ease-in",
      }}
    >
      <div className="px-2.5 py-1.5 bg-navy-wash border-b border-rule flex items-start justify-between">
        <div>
          <div className="font-display font-semibold text-navy" style={{ fontSize: 12.5, letterSpacing: 0 }}>
            {barName}
          </div>
          <div className="text-slate" style={{ fontSize: 10.5 }}>{cfg.note}</div>
          <div className="font-mono tabular-nums mt-0.5" style={{ fontWeight: 600, color: cfg.accent ? "#EA6A47" : "#1C4E80", fontSize: 13 }}>
            {fmtCurrency(cfg.total)}
          </div>
        </div>
        <button onClick={onClose} className="text-slate hover:text-navy px-1 text-2xl leading-none -mt-1" aria-label="Close" data-testid="waterfall-popover-close">×</button>
      </div>

      {/* Body — layout branches on isExpensesMaster */}
      {isExpensesMaster ? (
        <div className="flex flex-1 min-h-0" style={{ height: 320 }}>
          {/* LEFT pane — year list */}
          <div
            className="flex flex-col border-r border-rule"
            style={{ width: 250, minWidth: 250 }}
            data-testid="waterfall-expense-years-pane"
          >
            <div
              className="px-2 border-b border-rule text-slate flex items-center"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: "#F7FAFC",
                minHeight: 26,
              }}
            >
              Year · Inflated
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table className="w-full" style={{ fontFamily: "IBM Plex Mono", fontSize: 10.5 }}>
                <tbody>
                  {(() => {
                    // Peak Year = the year with the highest inflated expense
                    // in this list. Rendered gray (never orange) with a
                    // bold year and a "Peak Year" italic label between the
                    // year and the amount so Dave can eyeball it instantly.
                    const peakVal = rows.reduce((m, r) => (r.v > m ? r.v : m), 0);
                    const hasPeak = peakVal > 0;
                    return rows.map((r) => {
                      const isClickable = r.v > 0.5;
                      const isSelected = isClickable && r.year === activeYear;
                      const isPeak = hasPeak && r.v === peakVal;
                      return (
                        <tr
                          key={r.year}
                          className="border-b border-rule"
                          style={{
                            background: isSelected ? "#FCEAE2" : undefined,
                            borderLeft: isSelected ? "3px solid #EA6A47" : "3px solid transparent",
                          }}
                          data-testid={isPeak ? `waterfall-year-expense-peak-${r.year}` : undefined}
                        >
                          <td
                            className={
                              "px-2 py-0.5 tabular-nums " +
                              (isClickable ? "text-ink" : "text-ink/50")
                            }
                            style={{ fontWeight: 700 }}
                          >
                            {r.year}
                          </td>
                          <td
                            className="px-1 py-0.5 text-slate text-center"
                            style={{
                              fontFamily: "Source Sans 3, ui-sans-serif, system-ui",
                              fontSize: 10,
                              fontStyle: "italic",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isPeak ? "Peak Year" : ""}
                          </td>
                          <td
                            className={
                              "px-2 py-0.5 text-right tabular-nums text-ink " +
                              (isClickable
                                ? "cursor-pointer hover:underline hover:decoration-coral hover:decoration-1"
                                : "")
                            }
                            style={{
                              textUnderlineOffset: 2,
                              fontWeight: isSelected ? 700 : undefined,
                            }}
                            data-testid={
                              isClickable ? `waterfall-year-expense-${r.year}` : undefined
                            }
                            onClick={
                              isClickable
                                ? (e) => {
                                    e.stopPropagation();
                                    setSelectedYear(r.year);
                                  }
                                : undefined
                            }
                          >
                            {r.v === 0 ? "—" : fmtCurrency(r.v)}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-2.5 py-2 text-slate italic" style={{ fontSize: 10.5 }}>
                        No qualifying years.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT pane — element breakdown for the selected year */}
          <div
            className="flex flex-col flex-1 min-w-0"
            data-testid="waterfall-expense-detail-pane"
          >
            {/* Header — single-row layout so it matches the LEFT pane's
                header height (Dave 2026-02-08). Was two lines
                (title + "N elements replaced" subtitle); now the
                element count sits inline with the year, and the word
                "Expenses" is dropped from the title (the pane's
                context already makes that clear). */}
            <div
              className="px-2.5 border-b border-rule flex items-center justify-between"
              style={{ background: "#F7FAFC", minHeight: 26 }}
            >
              <div
                className="font-display font-semibold text-navy"
                style={{ fontSize: 11.5, letterSpacing: 0 }}
              >
                {activeYear != null ? `Year ${activeYear}` : "—"}
                {activeYearElements.length > 0 && (
                  <span
                    className="text-slate font-normal ml-2"
                    style={{ fontSize: 10 }}
                  >
                    · {activeYearElements.length} element{activeYearElements.length === 1 ? "" : "s"}
                  </span>
                )}
                {activeYear != null && activeYearElements.length === 0 && (
                  <span
                    className="text-slate font-normal ml-2 italic"
                    style={{ fontSize: 10 }}
                  >
                    · select a year on the left
                  </span>
                )}
              </div>
              {activeYearCal && (
                <div
                  className="font-mono tabular-nums"
                  style={{ fontWeight: 700, color: "#EA6A47", fontSize: 12.5 }}
                  data-testid="waterfall-expense-detail-total"
                >
                  {fmtCurrency(activeYearCal.amount)}
                </div>
              )}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {activeYearElements.length === 0 ? (
                <div
                  className="px-2.5 py-3 text-slate italic"
                  style={{ fontSize: 10.5 }}
                >
                  {activeYear != null
                    ? `No individual line items available for ${activeYear}.`
                    : "Click any year on the left to see its element breakdown."}
                </div>
              ) : (
                <table
                  className="w-full"
                  style={{ fontSize: 10.5 }}
                  data-testid="waterfall-expense-detail-table"
                >
                  <tbody>
                    {activeYearElements.map((el, i) => (
                      <tr
                        key={`${el.name}-${i}`}
                        className="border-b border-rule"
                        data-testid={`waterfall-expense-detail-row-${i}`}
                      >
                        <td
                          className="px-2 py-1 text-ink"
                          style={{
                            fontFamily: "'Source Sans Pro', ui-sans-serif, system-ui",
                            lineHeight: 1.25,
                          }}
                        >
                          {el.name}
                        </td>
                        <td
                          className="px-2 py-1 text-right tabular-nums text-navy"
                          style={{
                            fontFamily: "IBM Plex Mono, ui-monospace",
                            whiteSpace: "nowrap",
                            width: 88,
                          }}
                        >
                          {fmtCurrency(el.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {rows.length > 0 && (
            <div style={{ overflowY: "auto", maxHeight: 260 }}>
              <table className="w-full" style={{ fontFamily: "IBM Plex Mono", fontSize: 10.5 }}>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.year} className="border-b border-rule">
                      <td className="px-2 py-0.5 text-slate tabular-nums">{r.year}</td>
                      <td
                        className="px-2 py-0.5 text-right tabular-nums"
                        style={{ color: cfg.accent && r.v > 0 ? "#EA6A47" : undefined }}
                      >
                        {r.v === 0 ? "—" : fmtCurrency(r.v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length === 0 && !cfg.single && (
            <div className="px-2.5 py-2 text-slate" style={{ fontSize: 10.5 }}>No qualifying years.</div>
          )}
        </>
      )}
    </div>
  );

  return createPortal(popoverEl, document.body);
}

// (Previously had a click-open popover with per-year detail — replaced with a
// standard recharts hover Tooltip per Dave's request to mirror the Activity chart.)

// Hoisted so React sees a stable component identity across renders.
// Beginning / Ending render with a default cursor to signal they are
// informational totals and not interactive.
// Beginning becomes clickable (edit Opening Fund) when `editBeginning` is true.
const makeWaterfallBarShape = (editBeginning) => function WaterfallBarShape(props) {
  const { x, y, width, height, payload } = props;
  const editable = editBeginning && payload.name === "Beginning";
  const isTotal = payload.name === "Beginning" || payload.name === "Ending";
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={Math.max(height, 2)}
      fill={payload.fill}
      style={{ cursor: editable || !isTotal ? "pointer" : "default" }}
      data-testid={editable ? "waterfall-beginning-bar" : undefined}
    >
      {editable && <title>Click to edit Beginning Balance</title>}
    </rect>
  );
};

const waterfallLabelFormatter = (payload) => {
  if (!payload) return "";
  const v = payload.value;
  const abs = Math.abs(v);
  let s;
  if (abs >= 1e6) s = `$${(abs / 1e6).toFixed(2)}M`;
  else if (abs >= 1e3) s = `$${Math.round(abs / 1e3).toLocaleString()}K`;
  else s = `$${Math.round(abs)}`;
  const isTotal = payload.name === "Beginning" || payload.name === "Ending";
  if (isTotal) return s;
  if (v === 0) return "$0";
  return (v > 0 ? "+" : "-") + s;
};

const makeWaterfallLabelContent = (rows) => (props) => {
  const { x, y, width, index } = props;
  const row = rows[index];
  if (!row) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={10}
      fontFamily="IBM Plex Mono"
      fontWeight={600}
      fill="#1A1A1A"
    >
      {waterfallLabelFormatter(row)}
    </text>
  );
};

// Custom Tooltip cursor — the light-navy rectangle "hint" that Recharts
// draws over the hovered bar. Mirrors the Activity Chart's
// `cursor={{ fill: "rgba(28, 78, 128, 0.06)" }}` styling but suppresses
// itself over the Beginning / Ending bars (which have no click reveal),
// so only the 4 interactive columns (Contributions, Interest, Expenses,
// Special Asmt) show the hint. Added 2026-02-19 per Dave.
//
// 2026-02-19 (Dave, follow-up) — the previous "click for year-by-year
// detail" text tooltip has been removed; the tinted cursor above is now
// the sole click-affordance hint.
const makeWaterfallHoverCursor = (editBeginning) => function WaterfallHoverCursor(props) {
  const { x, y, width, height, payload } = props;
  const name = payload?.[0]?.payload?.name;
  if (name === "Ending" || (name === "Beginning" && !editBeginning)) return null;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill="rgba(28, 78, 128, 0.06)"
    />
  );
};

// Recharts 3 chart-level events expose activeLabel / activeTooltipIndex (no activePayload).
const isBeginningState = (st) => st?.activeLabel === "Beginning" || String(st?.activeTooltipIndex) === "0";

// Beginning → Contribs → Interest → Expenses → Special Asmts → Ending, over `horizon`.
export default function WaterfallChart({ calc, horizon, association, onBalanceSaved }) {
  const [active, setActive] = useState(null);
  const hover = useHoverIntent();
  const [editOpen, setEditOpen] = useState(false);
  const [overBeginning, setOverBeginning] = useState(false);
  const { user } = useAuth();
  const editBeginning = !!association && (canEdit(user) || canEditAssociation(user, association.tenant_id, association.aid));
  const barShape = useMemo(() => makeWaterfallBarShape(editBeginning), [editBeginning]);
  const hoverCursor = useMemo(() => makeWaterfallHoverCursor(editBeginning), [editBeginning]);
  if (!calc || !calc.projection?.length) return null;

  const slice = calc.projection.slice(0, horizon);
  const S = calc.display?.horizons?.[String(slice.length)] || {};
  const beginning = S.beginning || 0;
  const ending = S.ending || 0;
  const contributions = S.total_contributions || 0;
  const interest = S.total_interest || 0;
  const expenses = S.total_expenditures || 0;
  const specialAsmt = S.total_special_assessments || 0;

  let cursor = beginning;
  const rows = [
    { name: "Beginning", range: [0, beginning], value: beginning, fill: "#4A7BA5" },
  ];
  rows.push({
    name: "Contributions",
    range: [cursor, cursor + contributions],
    value: +contributions,
    fill: "#4A7BA5",
  });
  cursor += contributions;
  rows.push({
    name: "Interest",
    range: [cursor, cursor + interest],
    value: +interest,
    fill: "#88BFDE",
  });
  cursor += interest;
  rows.push({
    name: "Expenses",
    range: [cursor - expenses, cursor],
    value: -expenses,
    fill: "#0091D5",
  });
  cursor -= expenses;
  rows.push({
    name: "Special Asmt",
    range: [cursor, cursor + specialAsmt],
    value: +specialAsmt,
    fill: "#EA6A47",
  });
  cursor += specialAsmt;
  rows.push({ name: "Ending", range: [0, ending], value: ending, fill: "#4A7BA5" });

  return (
    <div
      className="panel-compact attention-border flex flex-col"
      data-testid="waterfall-chart"
      style={{ minHeight: 0, paddingBottom: 2 }}
    >
      <div className="dash-panel-header">
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
          Reserve Summary
        </div>
        <div
          className="text-[10.5px] font-semibold font-mono tabular-nums"
          style={{ color: "#4A5560" }}
          data-testid="waterfall-horizon-label"
        >
          {horizon} Year Horizon
        </div>
      </div>
      <div
        style={{ width: "100%", flex: 1, minHeight: 0, position: "relative" }}
        className="wf-nofocus"
      >
        <ResponsiveContainer>
          <BarChart
            data={rows}
            margin={{ top: 18, right: 10, bottom: 0, left: 4 }}
            barCategoryGap="32%"
            // Whole Beginning column is the click target (bar can be ~2px when the balance is $0).
            onClick={(st) => {
              if (editBeginning && isBeginningState(st)) setEditOpen(true);
            }}
            onMouseMove={(st) => setOverBeginning(editBeginning && isBeginningState(st))}
            onMouseLeave={() => setOverBeginning(false)}
            style={{ cursor: overBeginning ? "pointer" : undefined }}
          >
            <CartesianGrid stroke="#E8ECF1" vertical={false} />
            <XAxis dataKey="name" hide />
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Tooltip
              cursor={React.createElement(hoverCursor)}
              content={() => null}
              wrapperStyle={{ display: "none" }}
            />
            <Bar
              dataKey="range"
              minPointSize={2}
              shape={barShape}
              // 2026-02-23 (Dave) — disable bar animation so the top
              // value labels swap in place when calc updates (slider
              // nudge) instead of blinking off → on → off → on as
              // recharts tweens each bar's range.
              isAnimationActive={false}
              onMouseEnter={(data, _, event) => {
                if (data.name === "Beginning" || data.name === "Ending") return;
                const at = { name: data.name, x: event.clientX, y: event.clientY };
                hover.start(() => setActive(at));
              }}
              onMouseLeave={() => hover.cancel()}
              onClick={(data, _, event) => {
                hover.cancel();
                if (data.name === "Beginning" || data.name === "Ending") return;
                // Use viewport coords so the popover (portaled to
                // document.body + position:fixed) can render anywhere on
                // screen without being clipped by the waterfall panel.
                setActive({ name: data.name, x: event.clientX, y: event.clientY });
              }}
              style={{ cursor: "pointer" }}
            >
              <LabelList content={makeWaterfallLabelContent(rows)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {active && (
          <YearlyDetailPopover
            barName={active.name}
            slice={slice}
            calc={calc}
            onClose={() => setActive(null)}
            anchor={{ x: active.x, y: active.y }}
          />
        )}
        {editBeginning && (
          <OpeningBalanceModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            association={association}
            onSaved={onBalanceSaved}
          />
        )}
      </div>

      {/* Bottom rows: category names + per-year averages under each bar. */}
      <div
        className="border-t"
        style={{ borderColor: "#C9D6E2" }}
        data-testid="waterfall-average-row"
      >
        <div className="grid grid-cols-6 pt-0.5 text-[10.5px]">
          {["", "Contributions", "Interest", "Expenses", "Special Asmt", ""].map(
            (name, i) => (
              <div
                key={i}
                className={"text-center" + (i === 0 && editBeginning ? " cursor-pointer hover:text-navy underline-offset-2 hover:underline" : "")}
                style={{
                  color: "#5A6B75",
                  fontFamily: "Source Sans 3",
                  letterSpacing: 0.6,
                }}
                onClick={i === 0 && editBeginning ? () => setEditOpen(true) : undefined}
                title={i === 0 && editBeginning ? "Click to edit Beginning Balance" : undefined}
                data-testid={i === 0 ? "waterfall-beginning-caption" : undefined}
              >
                {i === 0 ? "Beginning" : i === 5 ? "Ending" : name}
              </div>
            ),
          )}
        </div>
        <div className="grid grid-cols-6 text-[10px]">
          {[
            { label: "Average", val: null, muted: true },
            { label: null, val: S.avg_contributions || 0, testid: "avg-contributions" },
            { label: null, val: S.avg_interest || 0, testid: "avg-interest" },
            { label: null, val: S.avg_expenditures || 0, testid: "avg-expenses" },
            { label: null, val: S.avg_special_assessments || 0, testid: "avg-special" },
            { label: null, val: null, muted: true },
          ].map((c, i) => (
            <div
              key={i}
              className="text-center font-mono tabular-nums"
              style={{
                color: c.muted ? "var(--u-slate)" : "var(--u-ink)",
                textTransform: c.label ? "uppercase" : "none",
                letterSpacing: c.label ? 1 : 0,
                fontWeight: c.label ? 600 : 500,
                fontSize: c.label ? 9 : 10,
              }}
              data-testid={c.testid}
            >
              {c.label ? c.label : c.val === null ? "" : fmtCurrency(c.val).replace(/\..*/, "")}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
